
import { useState } from "react";
import { KnowledgeCollection, KnowledgeArticle } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Book, BookOpen, BookMarked, FileText, 
  HelpCircle, Info, Settings, MessageCircle, 
  FileQuestion, Video, Lightbulb, Rocket, 
  Compass, MoreVertical, Plus, Edit, 
  Trash2, Eye, EyeOff
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Map of icon names to components
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  "Book": Book,
  "Book Open": BookOpen,
  "Book Marked": BookMarked,
  "File": FileText,
  "Help": HelpCircle,
  "Info": Info,
  "Settings": Settings,
  "Message": MessageCircle,
  "FAQ": FileQuestion,
  "Video": Video,
  "Lightbulb": Lightbulb,
  "Rocket": Rocket,
  "Compass": Compass,
};

type KnowledgeCollectionsListProps = {
  collections: KnowledgeCollection[];
  selectedCollectionId: string | null;
  onSelectCollection: (id: string) => void;
  onDeleteCollection: () => void;
  onCreateArticle: () => void;
  onSelectArticle: (id: string) => void;
  onArticleChange?: (collectionId: string, articles: KnowledgeArticle[]) => void;
};

const KnowledgeCollectionsList = ({
  collections,
  selectedCollectionId,
  onSelectCollection,
  onDeleteCollection,
  onCreateArticle,
  onSelectArticle,
  onArticleChange,
}: KnowledgeCollectionsListProps) => {
  const [expandedCollections, setExpandedCollections] = useState<string[]>([]);
  const [articles, setArticles] = useState<Record<string, KnowledgeArticle[]>>({});
  const { toast } = useToast();

  const toggleCollection = async (collectionId: string) => {
    // If not already expanded, fetch articles
    if (!expandedCollections.includes(collectionId)) {
      try {
        const { data, error } = await supabase
          .from("knowledge_articles")
          .select("*")
          .eq("collection_id", collectionId)
          .order("position", { ascending: true });

        if (error) {
          console.error("Error fetching articles:", error);
          toast({
            title: "Error",
            description: "Failed to load articles",
            variant: "destructive",
          });
          return;
        }

        const updatedArticles = {
          ...articles,
          [collectionId]: data || [],
        };
        
        setArticles(updatedArticles);
        
        // Notify parent component about article changes
        if (onArticleChange) {
          onArticleChange(collectionId, data || []);
        }
      } catch (error) {
        console.error("Unexpected error:", error);
      }
    }

    // Toggle expanded state
    setExpandedCollections((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  // Method to update article list when articles are added, updated or deleted
  const updateArticlesList = (collectionId: string, articleData?: any) => {
    if (!articleData) return;
    
    // Handle article deletion
    if (articleData.deleted) {
      setArticles(prev => {
        const collectionArticles = prev[collectionId] || [];
        const updatedArticles = collectionArticles.filter(article => article.id !== articleData.id);
        
        // Notify parent component about article changes
        if (onArticleChange) {
          onArticleChange(collectionId, updatedArticles);
        }
        
        return {
          ...prev,
          [collectionId]: updatedArticles
        };
      });
      return;
    }
    
    // Handle article creation or update
    setArticles(prev => {
      const collectionArticles = prev[collectionId] || [];
      
      // Check if this article already exists
      const existingIndex = collectionArticles.findIndex(article => article.id === articleData.id);
      
      let updatedArticles;
      if (existingIndex >= 0) {
        // Update existing article
        updatedArticles = [...collectionArticles];
        updatedArticles[existingIndex] = articleData;
      } else {
        // Add new article
        updatedArticles = [...collectionArticles, articleData];
      }
      
      // Notify parent component about article changes
      if (onArticleChange) {
        onArticleChange(collectionId, updatedArticles);
      }
      
      return {
        ...prev,
        [collectionId]: updatedArticles
      };
    });
  };

  const handleDeleteCollection = async (collectionId: string) => {
    if (!confirm("Are you sure you want to delete this collection and all its articles?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("knowledge_collections")
        .delete()
        .eq("id", collectionId);

      if (error) {
        console.error("Error deleting collection:", error);
        toast({
          title: "Error",
          description: "Failed to delete collection",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Collection deleted successfully",
      });
      
      // Also update the local state by removing this collection's articles
      setArticles(prev => {
        const updated = {...prev};
        delete updated[collectionId];
        return updated;
      });
      
      onDeleteCollection();
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const toggleCollectionPublished = async (collection: KnowledgeCollection) => {
    try {
      const { error } = await supabase
        .from("knowledge_collections")
        .update({ is_published: !collection.is_published })
        .eq("id", collection.id);

      if (error) {
        console.error("Error updating collection:", error);
        toast({
          title: "Error",
          description: "Failed to update collection",
          variant: "destructive",
        });
        return;
      }

      // Update local state
      const updatedCollections = collections.map((c) =>
        c.id === collection.id ? { ...c, is_published: !c.is_published } : c
      );

      toast({
        title: "Success",
        description: `Collection ${collection.is_published ? "unpublished" : "published"} successfully`,
      });
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  // Helper function to get the icon component for a collection
  const getIconComponent = (collection: KnowledgeCollection) => {
    const iconName = collection.icon || "Book";
    return ICON_MAP[iconName] || Book;
  };

  if (collections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Collections</CardTitle>
          <CardDescription>
            Create your first knowledge base collection to get started.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Accordion
        type="multiple"
        value={expandedCollections}
        className="w-full"
      >
        {collections.map((collection) => {
          const CollectionIcon = getIconComponent(collection);
          
          return (
            <AccordionItem
              key={collection.id}
              value={collection.id}
              className={`border rounded-lg ${
                selectedCollectionId === collection.id ? "bg-accent" : ""
              }`}
            >
              <div className="flex items-center justify-between p-4">
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => onSelectCollection(collection.id)}
                >
                  <CollectionIcon className="h-5 w-5 text-primary" />
                  <div>
                    <h3 className="font-medium">{collection.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {collection.is_published ? "Published" : "Draft"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <AccordionTrigger
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollection(collection.id);
                    }}
                    className="h-8 w-8 p-0 hover:bg-accent rounded-md"
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => toggleCollectionPublished(collection)}
                      >
                        {collection.is_published ? (
                          <>
                            <EyeOff className="mr-2 h-4 w-4" />
                            <span>Unpublish</span>
                          </>
                        ) : (
                          <>
                            <Eye className="mr-2 h-4 w-4" />
                            <span>Publish</span>
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteCollection(collection.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Delete</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <AccordionContent className="pt-0">
                <div className="space-y-2 pl-4 pr-2 pb-2">
                  {articles[collection.id]?.length > 0 ? (
                    articles[collection.id].map((article) => (
                      <div
                        key={article.id}
                        className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-accent ${
                          selectedCollectionId === collection.id ? "hover:bg-accent/80" : ""
                        }`}
                        onClick={() => onSelectArticle(article.id)}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{article.title}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {article.is_published ? "Published" : "Draft"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground p-2">
                      No articles in this collection
                    </p>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => {
                      onSelectCollection(collection.id);
                      onCreateArticle();
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Article
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default KnowledgeCollectionsList;
